import { combineReducers } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import ReviewImpactGridCreate from './ReviewImpactGrid/ReviewImpactGridCreate';

import {
  GET_REVIEW_IMPACT_ERROR,
  GET_REVIEW_IMPACT_START,
  GET_REVIEW_IMPACT_SUCCESS,
  GET_REVIEW_IMPACT_SELECTED_VIEW_ERROR,
  GET_REVIEW_IMPACT_SELECTED_VIEW_START,
  GET_REVIEW_IMPACT_SELECTED_VIEW_SUCCESS,
  GET_REVIEW_IMPACT_VIEWS_ERROR,
  GET_REVIEW_IMPACT_VIEWS_START,
  GET_REVIEW_IMPACT_VIEWS_SUCCESS,
  REVIEW_IMPACT_LOAD_MORE_ERROR,
  REVIEW_IMPACT_LOAD_MORE_START,
  REVIEW_IMPACT_LOAD_MORE_SUCCESS,
  SET_REVIEW_IMPACT_SELECTED_VIEW_START,
  SET_REVIEW_IMPACT_SELECTED_VIEW_SUCCESS,
  SET_REVIEW_IMPACT_SELECTED_VIEW_ERROR,
  TOGGLE_LOCATION
} from './ReviewImpactActions';

const initialState = {
  employeeData: null,
  loaded: false,
  loading: false,
  locationData: null,
  rowMetadata: {}, // { location: string, expanded: boolean, count: number, loading: bool, noMore: bool }
  reviewImpactSelectedView: {
    error: null,
    loaded: false,
    loading: false,
    selectedViewPk: null,
    status: null
  },
  reviewImpactViews: {
    defaultViewId: null,
    error: null,
    loaded: false,
    loading: false,
    status: null,
    views: []
  },
  totalNumberOfEmployees: null,
  totalNumberOfLocations: null,
  totalPaginationLocations: null
};

function buildRowMetadata(metadata, locationData, locationType) {
  const newMetadata = { ...metadata };

  if (metadata && locationData && locationType) {
    let locationIndex;
    if (locationType === 'work-location') {
      locationIndex = locationData?.terms?.findIndex(t => t.term === 'work_location_default');
    } else {
      locationIndex = locationData?.terms?.findIndex(t => t.term === 'city_state_default');
    }

    if (locationIndex) {
      locationData?.data?.forEach(row => {
        const location = row[locationIndex];

        if (location) {
          newMetadata[location] = {
            count: 3,
            expanded: true,
            loading: false,
            location,
            noMore: false
          };
        }
      });
    }
  }

  return newMetadata;
}

function reviewImpactReducer(state = initialState, action = {}) {
  switch (action.type) {
    case GET_REVIEW_IMPACT_START:
      return {
        ...state,
        loading: true
      };
    case GET_REVIEW_IMPACT_SUCCESS:
      return {
        ...state,
        employeeData: action.employeeData,
        loaded: true,
        loading: false,
        locationData: action.locationData,
        rowMetadata: buildRowMetadata(state.rowMetadata, action.locationData, action.locationType),
        totalNumberOfEmployees: action.totalNumberOfEmployees,
        totalNumberOfLocations: action.totalNumberOfLocations,
        totalPaginationLocations: action.totalPaginationLocations
      };
    case GET_REVIEW_IMPACT_ERROR:
      return {
        ...state,
        employeeData: null,
        loaded: true,
        loading: false,
        locationData: null,
        totalNumberOfEmployees: null,
        totalNumberOfLocations: null,
        totalPaginationLocations: null
      };
    case TOGGLE_LOCATION:
      const newToggleMetadata = cloneDeep(state.rowMetadata);
      if (newToggleMetadata[action.location]) {
        const expanded = newToggleMetadata[action.location].expanded;
        newToggleMetadata[action.location].expanded = !expanded;
      }

      return {
        ...state,
        rowMetadata: newToggleMetadata
      };
    case REVIEW_IMPACT_LOAD_MORE_START:
      const newLoadMetaStart = cloneDeep(state.rowMetadata);
      if (newLoadMetaStart[action.location]) {
        newLoadMetaStart[action.location].loading = true;
      }

      return {
        ...state,
        rowMetadata: newLoadMetaStart
      };
    case REVIEW_IMPACT_LOAD_MORE_SUCCESS:
      const newLoadMetaSuccess = cloneDeep(state.rowMetadata);
      const hasData = action.employeeData?.data?.length > 0;
      const noMore = action.employeeData?.data == null || action.employeeData?.data?.length < action.showMoreLimit;

      if (newLoadMetaSuccess[action.location]) {
        newLoadMetaSuccess[action.location].loading = false;

        if (noMore) {
          newLoadMetaSuccess[action.location].noMore = true;
        }
        if (hasData) {
          newLoadMetaSuccess[action.location].count += action.employeeData?.data?.length;
        }
      }

      const newEmployeeData = cloneDeep(state.employeeData);
      if (hasData) {
        newEmployeeData.data = newEmployeeData.data.concat(action.employeeData?.data);
      }

      return {
        ...state,
        employeeData: newEmployeeData,
        rowMetadata: newLoadMetaSuccess
      };
    case REVIEW_IMPACT_LOAD_MORE_ERROR:
      const newLoadError = cloneDeep(state.rowMetadata);
      if (newLoadError[action.location]) {
        newLoadError[action.location].loading = false;
      }

      return {
        ...state,
        rowMetadata: newLoadError
      };
    case GET_REVIEW_IMPACT_VIEWS_START: {
      return {
        ...state,
        reviewImpactViews: {
          views: state.reviewImpactViews?.views,
          defaultViewId: null,
          loading: true,
          loaded: false,
          status: null,
          error: null
        }
      };
    }
    case GET_REVIEW_IMPACT_VIEWS_SUCCESS: {
      return {
        ...state,
        reviewImpactViews: {
          views: action.views,
          defaultViewId: action.defaultViewId,
          loading: false,
          loaded: true,
          status: action.status || 200,
          error: null
        }
      };
    }
    case GET_REVIEW_IMPACT_VIEWS_ERROR: {
      return {
        ...state,
        reviewImpactViews: {
          views: [],
          defaultViewId: null,
          loading: false,
          loaded: true,
          status: action.status || 500,
          error: action.message
        }
      };
    }
    case GET_REVIEW_IMPACT_SELECTED_VIEW_START: {
      return {
        ...state,
        reviewImpactSelectedView: {
          selectedViewPk: state.reviewImpactSelectedView?.selectedViewPk,
          loading: true,
          loaded: false,
          status: null,
          error: null
        }
      };
    }
    case GET_REVIEW_IMPACT_SELECTED_VIEW_SUCCESS: {
      return {
        ...state,
        reviewImpactSelectedView: {
          selectedViewPk: action.selectedViewPk,
          loading: false,
          loaded: true,
          status: action.status || 200,
          error: null
        }
      };
    }
    case GET_REVIEW_IMPACT_SELECTED_VIEW_ERROR: {
      return {
        ...state,
        reviewImpactSelectedView: {
          selectedViewPk: null,
          loading: false,
          loaded: true,
          status: action.status,
          error: action.message
        }
      };
    }
    case SET_REVIEW_IMPACT_SELECTED_VIEW_START: {
      return {
        ...state,
        setReviewImpactSelectedView: {
          loading: true
        }
      };
    }
    case SET_REVIEW_IMPACT_SELECTED_VIEW_SUCCESS: {
      return {
        ...state,
        setReviewImpactSelectedView: {
          loading: false
        },
        reviewImpactSelectedView: {
          selectedViewPk: action.selectedViewPk,
          loading: false,
          loaded: true,
          status: action.status || 200,
          error: null
        }
      };
    }
    case SET_REVIEW_IMPACT_SELECTED_VIEW_ERROR: {
      return {
        ...state,
        setReviewImpactSelectedView: {
          loading: false
        }
      };
    }
    default:
      return state;
  }
}

export default combineReducers({
  reviewImpactReducer,
  reviewImpactGridReducer: ReviewImpactGridCreate
});
